import React, { useEffect, useState, useCallback } from "react";
import { Table, Tag, Spin, Empty, message, Alert, Modal, Input, Button } from "antd";
import { useSelector } from "react-redux";
import Breadcrumbs from "../../../../../components/Breadcrumbs";
import AttendanceReportFilter from "./components/AttendanceReportFilter";
import {
  useLazyGetDailyAttendanceQuery,
  useLazyGetAttendanceDetailsQuery,
  useAddAttendanceExceptionMutation,
} from "../../../../../services/attendance.service";
import { useLazyGetBranchesQuery, useLazyGetDepartmentsQuery } from "../../../../../services/settings.service";
import dayjs from "dayjs";
import ROLES from "../../../../../constants/roles";

const { TextArea } = Input;

const DailyAttendanceReport = () => {
  const user = useSelector((state) => state.user?.user || {});
  const userRole = user.role || "";
  const userBranchId = user?.branch?.id || null;
  const userDepartmentId = user?.employee.department?.id || null;

  const [data, setData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [dateColumns, setDateColumns] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Modal state for attendance details
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [attendanceInfo, setAttendanceInfo] = useState(null);

  // Modal state for marking as present
  const [markPresentModalVisible, setMarkPresentModalVisible] = useState(false);
  const [markPresentNote, setMarkPresentNote] = useState("");

  const [getDailyAttendance, { isLoading }] = useLazyGetDailyAttendanceQuery();
  const [getAttendanceDetails, { isLoading: isLoadingDetails }] = useLazyGetAttendanceDetailsQuery();
  const [addAttendanceException, { isLoading: isMarkingPresent }] = useAddAttendanceExceptionMutation();
  const [getBranches] = useLazyGetBranchesQuery();
  const [getDepartments] = useLazyGetDepartmentsQuery();

  // Check if user can mark attendance (HOD or Manager)
  const canMarkAttendance = [ROLES.HOD, ROLES.MANAGER].includes(userRole);

  // Set default branch/department for HOD and Manager roles
  useEffect(() => {
    if ([ROLES.HOD, ROLES.MANAGER].includes(userRole)) {
      if (userBranchId) setSelectedBranch(userBranchId);
      if (userDepartmentId && userRole === ROLES.HOD) setSelectedDepartment(userDepartmentId);
    }
  }, [userRole, userBranchId, userDepartmentId]);

  // Fetch branches and departments on mount
  useEffect(() => {
    fetchBranches();
    fetchDepartments();
  }, []);

  const fetchBranches = async () => {
    try {
      const result = await getBranches({ page: 1, limit: 100 });
      if (result.data?.success) {
        setBranches(result.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const result = await getDepartments({ page: 1, limit: 100 });
      if (result.data?.success) {
        setDepartments(result.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const fetchData = useCallback(async () => {
    // For HOD and Manager, branch_id is mandatory
    const branchToUse = [ROLES.HOD, ROLES.MANAGER].includes(userRole) ? (selectedBranch || userBranchId) : selectedBranch;
    const departmentToUse = userRole === ROLES.HOD ? (selectedDepartment || userDepartmentId) : selectedDepartment;

    const params = {
      start_date: dateRange[0].format("YYYY-MM-DD"),
      end_date: dateRange[1].format("YYYY-MM-DD"),
    };

    if (branchToUse) params.branch_id = branchToUse;
    if (departmentToUse) params.department_id = departmentToUse;

    try {
      const result = await getDailyAttendance(params);

      if (result.data?.success) {
        const reportData = result.data.data || [];
        setData(reportData);
        generateDateColumns();
        setHasSearched(true);
      } else {
        message.error("Failed to fetch attendance data");
      }
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      message.error("Error fetching attendance data");
    }
  }, [dateRange, selectedBranch, selectedDepartment, userRole, userBranchId, userDepartmentId]);

  // Handle clicking on present attendance cell to view details
  const handleAttendanceClick = async (record, dateStr, status) => {
    if (record.isDepartmentHeader) return;
    if (!status || status === "absent") return;

    setSelectedEmployee(record);
    setSelectedDate(dateStr);
    setModalVisible(true);

    try {
      const result = await getAttendanceDetails({
        employee_id: record.emp_id || record.id,
        date: dateStr,
      });

      if (result.data?.success) {
        setAttendanceInfo(result.data.data);
      } else {
        setAttendanceInfo(null);
      }
    } catch (error) {
      console.error("Error fetching attendance details:", error);
      setAttendanceInfo(null);
    }
  };

  // Handle clicking on absent cell to mark as present (for HOD/Manager only)
  const handleAbsentClick = (record, dateStr) => {
    if (record.isDepartmentHeader) return;
    if (!canMarkAttendance) return;

    setSelectedEmployee(record);
    setSelectedDate(dateStr);
    setMarkPresentNote("");
    setMarkPresentModalVisible(true);
  };

  // Submit mark as present
  const handleMarkAsPresent = async () => {
    if (!selectedEmployee || !selectedDate) return;

    try {
      const result = await addAttendanceException({
        employee_id: selectedEmployee.emp_id || selectedEmployee.id,
        date: selectedDate,
        status: "present",
        note: markPresentNote,
      });

      if (result.data?.success) {
        message.success("Employee marked as present successfully");
        setMarkPresentModalVisible(false);
        setMarkPresentNote("");
        // Refresh the data
        fetchData();
      } else {
        message.error(result.data?.message || "Failed to mark as present");
      }
    } catch (error) {
      console.error("Error marking as present:", error);
      message.error("Failed to mark as present");
    }
  };

  const generateDateColumns = () => {
    const start = dateRange[0];
    const end = dateRange[1];
    const columns = [];
    let current = start.clone();

    while (current.isBefore(end) || current.isSame(end, "day")) {
      const dateStr = current.format("YYYY-MM-DD");
      const dayNum = current.date();

      columns.push({
        title: (
          <div style={{ textAlign: "center" }}>
            <div>{dayNum}</div>
          </div>
        ),
        key: dateStr,
        width: 45,
        align: "center",
        render: (_, record) => {
          if (record.isDepartmentHeader) return null;
          const status = record.attendance?.[dateStr];

          const cellStyle = {
            fontWeight: "bold",
            fontSize: "14px",
          };

          if (status === "present" || status === true) {
            return (
              <span
                style={{ ...cellStyle, cursor: "pointer" }}
                onClick={() => handleAttendanceClick(record, dateStr, status)}
                title="Click to view details"
              >
                <i className="fa fa-check text-success" />
              </span>
            );
          } else if (status === "absent" || status === false) {
            return (
              <span
                style={{ ...cellStyle, cursor: canMarkAttendance ? "pointer" : "default" }}
                onClick={() => handleAbsentClick(record, dateStr)}
                title={canMarkAttendance ? "Click to mark as present" : ""}
              >
                <i className="fa fa-times text-danger" />
              </span>
            );
          } else if (status === "leave") {
            return <Tag color="blue" style={{ fontSize: 10, padding: "0 4px" }}>L</Tag>;
          } else if (status === "half" || status === "half_day") {
            return (
              <span
                style={{ ...cellStyle, cursor: "pointer" }}
                onClick={() => handleAttendanceClick(record, dateStr, status)}
                title="Click to view details"
              >
                <i className="fa fa-adjust text-warning" />
              </span>
            );
          }
          return null;
        },
      });

      current = current.add(1, "day");
    }

    setDateColumns(columns);
  };

  const handleSearch = () => {
    fetchData();
  };

  const handleExportPDF = () => {
    const branchToUse = [ROLES.HOD, ROLES.MANAGER].includes(userRole) ? (selectedBranch || userBranchId) : selectedBranch;
    const departmentToUse = userRole === ROLES.HOD ? (selectedDepartment || userDepartmentId) : selectedDepartment;

    window.open(
      `/api/v2/attendance/reports/daily?start_date=${dateRange[0].format("YYYY-MM-DD")}&end_date=${dateRange[1].format("YYYY-MM-DD")}&format=pdf${branchToUse ? `&branch_id=${branchToUse}` : ""}${departmentToUse ? `&department_id=${departmentToUse}` : ""}`,
      "_blank"
    );
  };

  const handleExportExcel = () => {
    const branchToUse = [ROLES.HOD, ROLES.MANAGER].includes(userRole) ? (selectedBranch || userBranchId) : selectedBranch;
    const departmentToUse = userRole === ROLES.HOD ? (selectedDepartment || userDepartmentId) : selectedDepartment;

    window.open(
      `/api/v2/attendance/reports/daily?start_date=${dateRange[0].format("YYYY-MM-DD")}&end_date=${dateRange[1].format("YYYY-MM-DD")}&format=excel${branchToUse ? `&branch_id=${branchToUse}` : ""}${departmentToUse ? `&department_id=${departmentToUse}` : ""}`,
      "_blank"
    );
  };

  const handlePrint = () => {
    window.print();
  };

  // Fixed columns for employee info
  const fixedColumns = [
    {
      title: "Emp ID",
      dataIndex: "employee_number",
      key: "employee_number",
      width: 80,
      fixed: "left",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      fixed: "left",
      render: (text, record) => (
        <span>{text || `${record.first_name || ""} ${record.last_name || ""}`}</span>
      ),
    },
  ];

  // Flatten data for table - group by department
  const flattenedData = [];
  data.forEach((dept, deptIndex) => {
    // Add department header row
    flattenedData.push({
      key: `dept-${deptIndex}`,
      isDepartmentHeader: true,
      department_name: dept.department_name || dept.department,
    });

    // Add employee rows
    (dept.employees || []).forEach((emp, empIndex) => {
      // Filter by employee search
      const empName = emp.name || emp.employee_name || `${emp.first_name || ""} ${emp.last_name || ""}`;
      if (employeeSearch && !empName.toLowerCase().includes(employeeSearch.toLowerCase())) {
        return;
      }

      // Transform daily_status to attendance format expected by columns
      const attendance = {};
      const dailyStatus = emp.daily_status || emp.attendance || {};
      Object.keys(dailyStatus).forEach((dateKey) => {
        const dayData = dailyStatus[dateKey];
        // Handle both formats: { status: "absent" } or just "absent"
        attendance[dateKey] = typeof dayData === "object" ? dayData.status : dayData;
      });

      flattenedData.push({
        key: `emp-${deptIndex}-${empIndex}`,
        ...emp,
        attendance,
      });
    });
  });

  const columns = [...fixedColumns, ...dateColumns];

  const getReportTitle = () => {
    const start = dateRange[0].format("D");
    const end = dateRange[1].format("D");
    const month = dateRange[0].format("MMM");
    return `Daily Attendance Report for ${start} ${month} - ${end} ${month}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "-";
    return dayjs(timeStr).format("hh:mm A");
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={getReportTitle()}
          title="Attendance Report"
          subtitle="Daily Attendance"
          showButton={false}
        />

        <AttendanceReportFilter
          branches={branches}
          departments={departments}
          selectedBranch={selectedBranch}
          selectedDepartment={selectedDepartment}
          dateRange={dateRange}
          employeeSearch={employeeSearch}
          onBranchChange={setSelectedBranch}
          onDepartmentChange={setSelectedDepartment}
          onDateRangeChange={setDateRange}
          onEmployeeSearchChange={setEmployeeSearch}
          onSearch={handleSearch}
          onExportPDF={handleExportPDF}
          onExportExcel={handleExportExcel}
          onPrint={handlePrint}
          isLoading={isLoading}
          showEmployeeSearch={true}
          userRole={userRole}
          hasSearched={hasSearched}
        />

        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                {isLoading ? (
                  <div className="text-center py-5">
                    <Spin size="large" />
                  </div>
                ) : !hasSearched ? (
                  <Alert
                    message="Select filters and click SEARCH to display the report"
                    type="info"
                    showIcon
                    style={{ maxWidth: 500, margin: "50px auto" }}
                  />
                ) : flattenedData.length === 0 ? (
                  <Empty description="No attendance data found" />
                ) : (
                  <Table
                    columns={columns}
                    dataSource={flattenedData}
                    pagination={false}
                    scroll={{ x: "max-content" }}
                    size="small"
                    bordered
                    rowClassName={(record) =>
                      record.isDepartmentHeader ? "department-header-row" : ""
                    }
                    onRow={(record) => ({
                      style: record.isDepartmentHeader
                        ? {
                            backgroundColor: "#f5f5f5",
                            fontWeight: "bold",
                          }
                        : {},
                    })}
                    components={{
                      body: {
                        row: ({ children, ...restProps }) => {
                          const record = flattenedData.find(
                            (d) => d.key === restProps["data-row-key"]
                          );
                          if (record?.isDepartmentHeader) {
                            return (
                              <tr {...restProps}>
                                <td
                                  colSpan={columns.length}
                                  style={{
                                    backgroundColor: "#e8e8e8",
                                    fontWeight: "bold",
                                    padding: "8px 12px",
                                  }}
                                >
                                  {record.department_name}
                                </td>
                              </tr>
                            );
                          }
                          return <tr {...restProps}>{children}</tr>;
                        },
                      },
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Attendance Details Modal */}
        <Modal
          title={`Attendance Info (${selectedEmployee?.name || selectedEmployee?.employee_name || ""})`}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setAttendanceInfo(null);
          }}
          footer={null}
          width={600}
        >
          <div className="row">
            <div className="col-md-6">
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-3">
                    Timesheet {selectedDate ? dayjs(selectedDate).format("Do MMM YYYY") : ""}
                  </h6>
                  {isLoadingDetails ? (
                    <div className="text-center py-3">
                      <Spin />
                    </div>
                  ) : (
                    <>
                      <div className="mb-2 p-2 bg-light rounded">
                        <strong>Punch In at</strong>
                        <div>{attendanceInfo?.first_in ? formatTime(attendanceInfo.first_in) : "-"}</div>
                      </div>

                      <div className="text-center my-4">
                        <div
                          style={{
                            width: 120,
                            height: 120,
                            borderRadius: "50%",
                            border: "8px solid #e8e8e8",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto",
                          }}
                        >
                          <span style={{ fontSize: 18 }}>
                            {attendanceInfo?.total_hours || "0"} hrs
                          </span>
                        </div>
                      </div>

                      <div className="mb-2 p-2 bg-light rounded">
                        <strong>Punch Out at</strong>
                        <div>{attendanceInfo?.last_out ? formatTime(attendanceInfo.last_out) : "-"}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="mb-3">Activity</h6>
                  {isLoadingDetails ? (
                    <div className="text-center py-3">
                      <Spin />
                    </div>
                  ) : attendanceInfo?.punches?.length > 0 ? (
                    <div style={{ maxHeight: 300, overflowY: "auto" }}>
                      {attendanceInfo.punches.map((punch, index) => (
                        <div key={index} className="mb-2 p-2 border-bottom">
                          <div className="d-flex justify-content-between">
                            <span className={punch.punch_type === "IN" ? "text-success" : "text-danger"}>
                              {punch.punch_type === "IN" ? "Punch In" : "Punch Out"}
                            </span>
                            <span>{formatTime(punch.punch_time)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Empty description="No activity records" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </Modal>

        {/* Mark as Present Modal */}
        <Modal
          title={`Mark as Present - ${selectedEmployee?.name || selectedEmployee?.employee_name || ""}`}
          open={markPresentModalVisible}
          onCancel={() => {
            setMarkPresentModalVisible(false);
            setMarkPresentNote("");
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setMarkPresentModalVisible(false);
                setMarkPresentNote("");
              }}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={isMarkingPresent}
              onClick={handleMarkAsPresent}
            >
              Mark as Present
            </Button>,
          ]}
        >
          <div className="mb-3">
            <p>
              <strong>Employee:</strong> {selectedEmployee?.name || selectedEmployee?.employee_name || ""}
            </p>
            <p>
              <strong>Employee ID:</strong> {selectedEmployee?.employee_number || ""}
            </p>
            <p>
              <strong>Date:</strong> {selectedDate ? dayjs(selectedDate).format("dddd, Do MMMM YYYY") : ""}
            </p>
          </div>
          <div className="mb-3">
            <label className="form-label">
              <strong>Note / Reason</strong> <span className="text-muted">(optional)</span>
            </label>
            <TextArea
              rows={4}
              placeholder="Enter reason for marking as present (e.g., field work, meeting, etc.)"
              value={markPresentNote}
              onChange={(e) => setMarkPresentNote(e.target.value)}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default DailyAttendanceReport;
